import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Download, Building2, Image, MapPin, Phone, Mail, Globe } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Static data untuk halaman 1 (Holding Company)
const holdingCompany = {
  id: 1,
  name: "PT GPB Grha Pendaringan Banda",
  shortName: "PT GPB",
  sector: "Holding / Strategic Investment",
  description: "PT GPB Grha Pendaringan Banda adalah perusahaan holding yang berperan sebagai pengelola strategis dan pengendali tata kelola grup usaha lintas sektor, dengan fokus pada sinergi bisnis, efisiensi operasional, kepatuhan hukum, serta penciptaan nilai jangka panjang melalui manajemen terpusat.",
  points: [
    "Pengelolaan dan pengendalian grup usaha",
    "Perumusan strategi bisnis dan investasi",
    "Integrasi operasional antar entitas",
    "Tata kelola, kepatuhan, dan manajemen risiko",
    "Pengembangan nilai jangka panjang grup"
  ],
  logo: "/images/PT GPB Grha Pendaringan Banda.jpg"
};

const sisterCompanies = [
  { id: 2, name: "Bang Toyib", logo: "/images/PT Bang Toyib Travel.jpg" },
  { id: 3, name: "DGY", logo: "/images/PT Damel Gadha Yatra.jpg" },
  { id: 4, name: "LSA", logo: "/images/PT Lintas Sinergi Andalan.jpg" },
  { id: 5, name: "MSI", logo: "/images/PT Meva Sri Iwak.jpg" },
  { id: 6, name: "HSEO", logo: "/images/PT HSEO Grha Tekno.jpg" },
  { id: 7, name: "Telemitra", logo: "/images/PT Telemitra Globalindo.jpg" },
  { id: 8, name: "UJB", logo: "/images/PT Urusan Jaga Banda.jpg" },
  { id: 9, name: "GMM", logo: "/images/PT Grha Mizan Manikam.jpg" },
  { id: 10, name: "JUM", logo: "/images/PT Jayakarta Utama Mandiri.jpg" },
  { id: 11, name: "FLF", logo: "/images/Faruq Law Firm.jpg" }
];

const LandingPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [subsidiaryProfiles, setSubsidiaryProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const res = await axios.get(`${API}/api/website-profiles/all`);
        const sorted = res.data.filter(p => p.slide_id >= 2).sort((a, b) => a.slide_id - b.slide_id);
        setSubsidiaryProfiles(sorted);
      } catch (err) {
        console.error('Failed to fetch profiles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfiles();
  }, []);

  const totalSlides = 1 + subsidiaryProfiles.length;

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  const handleDownload = () => { window.print(); };

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { zIndex: 1, x: 0, opacity: 1 },
    exit: (dir) => ({ zIndex: 0, x: dir < 0 ? '100%' : '-100%', opacity: 0 })
  };

  if (loading) {
    return (
      <div className="slide-container batik-pattern" style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
        <div style={{color:'#29ABE2',fontSize:'16px'}}>Memuat...</div>
      </div>
    );
  }

  return (
    <div className="slide-container batik-pattern" data-testid="landing-page">
      <button className="nav-arrow nav-arrow-left" onClick={prevSlide} disabled={currentSlide === 0}>
        <ChevronLeft size={22} />
      </button>
      <button className="nav-arrow nav-arrow-right" onClick={nextSlide} disabled={currentSlide === totalSlides - 1}>
        <ChevronRight size={22} />
      </button>

      <AnimatePresence initial={false} custom={direction} mode="wait">
        <motion.div
          key={currentSlide}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }}
          className="slide-content kawung-pattern"
        >
          {currentSlide === 0 ? (
            <HoldingSlide company={holdingCompany} />
          ) : (
            <SubsidiarySlide profile={subsidiaryProfiles[currentSlide - 1]} />
          )}
        </motion.div>
      </AnimatePresence>

      <div className="page-indicator">{currentSlide + 1} / {totalSlides}</div>
      <button className="download-btn" onClick={handleDownload}>
        <Download size={14} />
        <span>Download PDF</span>
      </button>
    </div>
  );
};

// ============ HALAMAN 1 - HOLDING COMPANY ============
const HoldingSlide = ({ company }) => {
  return (
    <div style={styles.slideInner}>
      <div className="decorative-border"></div>
      
      <div style={styles.content}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>{company.name}</h1>
          <div style={styles.sectorWrap}>
            <div className="gold-accent"></div>
            <span style={styles.sector}>{company.sector}</span>
            <div className="gold-accent"></div>
          </div>
        </div>

        {/* Main Content - 2 columns */}
        <div style={styles.mainGrid}>
          {/* Left - Circle Hub */}
          <div style={styles.circleHubWrap}>
            <div style={styles.circleHub}>
              {/* Center Logo */}
              <div style={styles.centerLogo}>
                <img src={company.logo} alt={company.name} style={styles.logoImg} onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              
              {/* Sister Companies Circle */}
              {sisterCompanies.map((sister, index) => {
                const angle = (index * 36) - 90;
                const radius = 95;
                const x = Math.cos((angle * Math.PI) / 180) * radius;
                const y = Math.sin((angle * Math.PI) / 180) * radius;
                
                return (
                  <div key={sister.id} style={{
                    ...styles.sisterLogo,
                    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`
                  }} title={sister.name}>
                    <img src={sister.logo} alt={sister.name} style={styles.sisterImg} onError={(e) => { 
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }} />
                    <div style={styles.sisterFallback}>{sister.name.substring(0,3)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right - Description */}
          <div style={styles.rightContent}>
            <h2 style={styles.sectionTitle}>Deskripsi</h2>
            <p style={styles.description}>{company.description}</p>
            
            <h2 style={{...styles.sectionTitle, marginTop: '16px'}}>Poin Utama</h2>
            <ul className="bullet-list">
              {company.points.map((point, i) => <li key={i}>{point}</li>)}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <span style={styles.footerText}>PT GPB Grha Pendaringan Banda Group</span>
          <span style={styles.footerText}>{new Date().getFullYear()} - Company Profile</span>
        </div>
      </div>
    </div>
  );
};

// ============ HALAMAN 2-11 - SUBSIDIARY COMPANIES ============
const SubsidiarySlide = ({ profile }) => {
  if (!profile) return <div>Loading...</div>;

  const logoSrc = profile.logo_filename 
    ? `${API}/api/website-profile/image/${profile.logo_filename}`
    : profile.logo_path || '/images/placeholder.jpg';

  const galeriImages = profile.galeri || [null, null, null, null];

  return (
    <div style={styles.slideInner}>
      <div className="decorative-border"></div>
      <div className="wayang-decoration"></div>
      
      <div style={styles.content}>
        {/* Header */}
        <div style={styles.subHeader}>
          <div style={styles.logoBox}>
            <img src={logoSrc} alt={profile.nama_web} style={styles.logoImg} onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <div style={styles.headerInfo}>
            <h1 style={styles.subTitle}>{profile.nama_web}</h1>
            {profile.short_name && <p style={styles.shortName}>{profile.short_name}</p>}
            <div style={styles.sectorWrap}>
              <div className="gold-accent"></div>
              <span style={styles.sector}>{profile.sector}</span>
            </div>
          </div>
        </div>

        {/* Main Content - 2 columns */}
        <div style={styles.subMainGrid}>
          {/* Left - Gallery */}
          <div style={styles.galleryGrid}>
            {[0, 1, 2, 3].map((num) => {
              const galeriFile = galeriImages[num];
              const imgSrc = galeriFile ? `${API}/api/website-profile/image/${galeriFile}` : null;
              return (
                <div key={num} className="image-placeholder">
                  {imgSrc ? (
                    <img src={imgSrc} alt={`Gambar ${num+1}`} style={{width:'100%',height:'100%',objectFit:'cover'}} />
                  ) : (
                    <>
                      <Image size={20} style={{opacity:0.4,marginBottom:'4px'}} />
                      <span>Gambar {num + 1}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right - Info */}
          <div style={styles.rightContent}>
            <h2 style={styles.sectionTitle}>Deskripsi</h2>
            <p style={styles.descriptionSmall}>{profile.deskripsi}</p>
            
            <h2 style={{...styles.sectionTitle, marginTop: '12px'}}>Poin Utama</h2>
            <ul className="bullet-list">
              {(profile.poin || []).map((point, i) => <li key={i}>{point}</li>)}
            </ul>

            {/* Contact Info */}
            <div style={styles.contactGrid}>
              {profile.alamat && (
                <div style={styles.contactItem}>
                  <MapPin size={12} style={{color:'#29ABE2',flexShrink:0}} />
                  <span style={styles.contactText}>{profile.alamat}</span>
                </div>
              )}
              {profile.telepon && (
                <div style={styles.contactItem}>
                  <Phone size={12} style={{color:'#29ABE2',flexShrink:0}} />
                  <span style={styles.contactText}>{profile.telepon}</span>
                </div>
              )}
              {profile.email && (
                <div style={styles.contactItem}>
                  <Mail size={12} style={{color:'#29ABE2',flexShrink:0}} />
                  <span style={styles.contactText}>{profile.email}</span>
                </div>
              )}
              {profile.website && (
                <div style={styles.contactItem}>
                  <Globe size={12} style={{color:'#29ABE2',flexShrink:0}} />
                  <span style={styles.contactText}>{profile.website}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <span style={styles.footerText}>PT GPB Grha Pendaringan Banda Group</span>
          <span style={styles.footerText}>{new Date().getFullYear()} - Company Profile</span>
        </div>
      </div>
    </div>
  );
};

// ============ STYLES ============
const styles = {
  slideInner: {
    width: '100%',
    height: '100%',
    position: 'relative',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column'
  },
  content: {
    flex: 1,
    padding: '20px 28px 16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 1
  },
  header: {
    textAlign: 'center',
    marginBottom: '12px'
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#1e3a5f',
    margin: '0 0 8px',
    fontFamily: 'Montserrat, sans-serif'
  },
  sectorWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px'
  },
  sector: {
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '4px 14px',
    background: 'rgba(201, 162, 39, 0.1)',
    color: '#1e3a5f',
    borderRadius: '20px',
    border: '1px solid rgba(201, 162, 39, 0.3)'
  },
  mainGrid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    minHeight: 0
  },
  circleHubWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  circleHub: {
    position: 'relative',
    width: '250px',
    height: '250px'
  },
  centerLogo: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    border: '3px solid #29ABE2',
    background: '#fff',
    overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  logoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  sisterLogo: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    border: '2px solid #39B54A',
    background: '#fff',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s'
  },
  sisterImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  sisterFallback: {
    display: 'none',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '8px',
    fontWeight: 700,
    color: '#39B54A',
    background: '#f0f8f4'
  },
  rightContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  sectionTitle: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#29ABE2',
    margin: '0 0 8px',
    fontFamily: 'Montserrat, sans-serif'
  },
  description: {
    fontSize: '0.8rem',
    color: '#4a4a4a',
    lineHeight: 1.6,
    margin: 0
  },
  descriptionSmall: {
    fontSize: '0.75rem',
    color: '#4a4a4a',
    lineHeight: 1.5,
    margin: 0
  },
  footer: {
    marginTop: 'auto',
    paddingTop: '12px',
    borderTop: '1px solid rgba(41, 171, 226, 0.15)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: '0.65rem',
    color: '#4a4a4a'
  },
  // Subsidiary specific
  subHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '12px'
  },
  logoBox: {
    width: '60px',
    height: '60px',
    borderRadius: '10px',
    border: '2px solid rgba(57, 181, 74, 0.3)',
    overflow: 'hidden',
    flexShrink: 0
  },
  headerInfo: {
    flex: 1
  },
  subTitle: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#29ABE2',
    margin: '0 0 2px',
    fontFamily: 'Montserrat, sans-serif'
  },
  shortName: {
    fontSize: '0.75rem',
    color: '#39B54A',
    margin: '0 0 6px',
    fontWeight: 500
  },
  subMainGrid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '45% 55%',
    gap: '16px',
    minHeight: 0
  },
  galleryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    gap: '8px'
  },
  contactGrid: {
    marginTop: '12px',
    paddingTop: '10px',
    borderTop: '1px solid #eee',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px'
  },
  contactItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px'
  },
  contactText: {
    fontSize: '0.65rem',
    color: '#4a4a4a',
    lineHeight: 1.3
  }
};

export default LandingPage;
